import {
  Box,
  Button,
  CloseIcon,
  FortoEmissionOffsetBadgeDarkIcon,
  IconButton,
  makeStyles,
  Paper,
  StatusBox,
  Theme,
  Typography,
} from '@freighthub/silhouette-core'
import csx from 'classnames'
import { FC } from 'react'
import { useApi } from 'service/useApi'
import { useI18n } from 'utils/localisation'

import { useCustomerPreference } from './CustomerPreference/CustomerPreferenceContext'
import { CarbonOffsetStrategy } from './QuoteServices'

type DefaultOffsetDialogProps = {
  onClose: () => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    maxWidth: theme.breakpoints.values.md,
    padding: theme.spacing(4),

    [theme.breakpoints.down('sm')]: {
      left: theme.spacing(1),
      right: theme.spacing(1),
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bodyText: {
    color: theme.palette.text.primary,
  },
  marginBottom: {
    marginBottom: theme.spacing(1),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  boldText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  successButton: {
    backgroundColor: theme.palette.status.success,
    '&:hover': {
      backgroundColor: theme.palette.status.success,
    },
  },
  asterisk: {
    color: theme.palette.status.error,
  },
  sustainabilityIcon: {
    width: 50,
    height: 50,
  },
}))

export const DefaultOffsetDialog: FC<DefaultOffsetDialogProps> = ({ onClose }) => {
  const classes = useStyles()
  const API = useApi()
  const { customerPreference, setCustomerPreference } = useCustomerPreference()
  const { translate } = useI18n()

  const optedIn = customerPreference?.sustainability?.offsetShipmentsByDefault === CarbonOffsetStrategy.All

  const updateShipmentOffsetSetting = (strategy: CarbonOffsetStrategy) => async () => {
    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    customerPreference.sustainability.offsetShipmentsByDefault = strategy
    // @ts-expect-error TS(2345) FIXME: Argument of type 'CustomerPreference | null' is no... Remove this comment to see the full error message
    const updatedPreference = await API.customers.updateCustomerPreference(customerPreference)
    // @ts-expect-error TS(2345) FIXME: Argument of type 'CustomerPreference | null' is no... Remove this comment to see the full error message
    setCustomerPreference(updatedPreference)
  }

  const optIn = updateShipmentOffsetSetting(CarbonOffsetStrategy.All)
  const optOut = updateShipmentOffsetSetting(CarbonOffsetStrategy.None)

  return (
    <Paper variant="elevation" className={classes.container}>
      <IconButton
        data-tracking-id="default-offset-dialog-close-button"
        onClick={onClose}
        className={classes.closeButton}
      >
        <CloseIcon />
      </IconButton>

      {!optedIn ? (
        <>
          <div className={csx(classes.header, classes.marginBottom)}>
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
              <FortoEmissionOffsetBadgeDarkIcon className={classes.sustainabilityIcon} />
              <Typography variant="h5">{translate('default_biofuel-opted_out-title')}</Typography>
            </Box>
          </div>

          <Typography variant="body2" className={csx(classes.bodyText, classes.marginBottom)}>
            {translate('default_biofuel-opted_out-body')}
          </Typography>

          <Typography variant="body2" className={csx(classes.bodyText, classes.marginBottom)}>
            {translate('default_biofuel-opted_out-body-reminder')}
          </Typography>

          <Typography variant="body1" className={csx(classes.boldText, classes.marginBottom)}>
            {translate('default_biofuel-opted_out-info')}
          </Typography>
          <Button
            data-tracking-id="default-offset-dialog-optin"
            onClick={optIn}
            className={csx(classes.successButton, classes.marginRight, classes.marginBottom)}
            variant="contained"
          >
            {translate('default_biofuel-button_on')}
          </Button>

          <Typography component="p" variant="caption">
            <span className={classes.asterisk}>*</span> {translate('default_biofuel-off_subtitle')}
          </Typography>
        </>
      ) : (
        <>
          <div className={csx(classes.header, classes.marginBottom)}>
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
              <FortoEmissionOffsetBadgeDarkIcon className={classes.sustainabilityIcon} />
              <Typography variant="h5">{translate('default_biofuel-opted_in-title')}</Typography>
            </Box>
          </div>

          <Typography variant="body2" className={csx(classes.bodyText, classes.marginBottom)}>
            {translate('default_biofuel-opted_in-body')}
          </Typography>

          <StatusBox fullWidth variant="success" icon className={classes.marginBottom}>
            {translate('default_biofuel-on_message')}
          </StatusBox>
          <Box>
            <Button
              data-tracking-id="default-offset-dialog-optout"
              onClick={optOut}
              className={csx(classes.marginBottom, classes.marginRight)}
              variant="outlined"
            >
              {translate('default_biofuel-button_off')}
            </Button>
          </Box>
          <Typography component="p" variant="caption">
            <span className={classes.asterisk}>*</span> {translate('default_biofuel-on_subtitle')}
          </Typography>
        </>
      )}
    </Paper>
  )
}
